import { apiClient } from "../client";
import { ENDPOINTS } from "../endpoints";

// KX-39 (T-32): KYC moved to the payout boundary. The operator goes live and
// accrues a pending balance WITHOUT uploading anything; identity is only
// required at the first withdrawal. The gate itself is enforced in backend code
// (403 KYC_REQUIRED on withdraw) — this service only reads status and submits
// documents; it never decides whether a payout is allowed.

export type KycStatus = "not_started" | "pending_review" | "verified" | "rejected";

export type KycDocumentType = "id_front" | "id_back" | "proof_of_address" | "selfie";

// Q1 — GET /api/operator/kyc/status
export interface KycStatusResponse {
  status: KycStatus;
  rejectionReason: string | null;
  requiredDocuments: KycDocumentType[];
  governmentIdRequired: boolean;
  submittedAt: string | null;
  reviewedAt: string | null;
}

// Q2 — POST /api/operator/kyc/submit
export interface KycSubmitPayload {
  fullName: string;
  governmentIdType: string;
  idNumber: string;
  documents: {
    id_front: string;
    id_back: string;
    proof_of_address: string;
    selfie: string;
  };
}

export interface KycSubmitResponse {
  success: boolean;
  status: KycStatus;
  message?: string;
}

// A structured error the UI can branch on. The submit endpoint returns:
//  - 400 KYC_INCOMPLETE (with the missing document list)
//  - 409 KYC_UNDER_REVIEW / already verified
export interface KycApiError {
  code?: string;
  message?: string;
  missing?: KycDocumentType[];
  status?: number;
}

export const parseKycError = (e: unknown): KycApiError => {
  const err = e as {
    response?: { status?: number; data?: { code?: string; message?: string; data?: { missing?: KycDocumentType[] } } };
    message?: string;
  };
  return {
    status: err?.response?.status,
    code: err?.response?.data?.code,
    message: err?.response?.data?.message || err?.message,
    missing: err?.response?.data?.data?.missing,
  };
};

export const operatorKycService = {
  // Q1: read the operator's current KYC state.
  getStatus: async (): Promise<KycStatusResponse> => {
    const res = await apiClient.get<{ success?: boolean; data?: KycStatusResponse } | KycStatusResponse>(
      ENDPOINTS.OPERATOR.KYC_STATUS
    );
    // Tolerate both {data:{...}} and a flat payload.
    const body = res.data as { data?: KycStatusResponse } & Partial<KycStatusResponse>;
    return (body.data ?? (body as KycStatusResponse));
  },

  // Q2: submit identity + document URLs → moves status to pending_review.
  submit: async (payload: KycSubmitPayload): Promise<KycSubmitResponse> => {
    const res = await apiClient.post<KycSubmitResponse>(ENDPOINTS.OPERATOR.KYC_SUBMIT, payload);
    return res.data;
  },

  // Document upload via the purpose-built KYC endpoint (decoupled from
  // registration — no registrationId). Documents go to a separate Cloudinary
  // folder; the handler returns a hosted URL we then pass to submit().
  uploadDocument: async (documentType: KycDocumentType, file: File): Promise<string> => {
    const formData = new FormData();
    formData.append("documentType", documentType);
    formData.append("file", file);
    const res = await apiClient.post<{ success?: boolean; data?: { documentType: string; documentUrl: string; documentId: string } }>(
      ENDPOINTS.OPERATOR.KYC_UPLOAD_DOCUMENT,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } }
    );
    const url = res.data?.data?.documentUrl;
    if (!url) throw new Error("Upload did not return a document URL");
    return url;
  },
};

export default operatorKycService;
